/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * SUBMISSION IS A HARD STOP FOR CLIENT-SIDE QUESTION GENERATION (v1_653).
 *
 * The pre-submit readiness review runs asynchronously the moment Alex first
 * invites SUBMIT. If the client types SUBMIT before it returns, the review used
 * to inject a fresh "a few last points to lock down" question round INTO THE
 * ALREADY-SUBMITTED chat, because its guard read a stale closure copy of the
 * submitted flag (Sage Capital, 17/09/26). Once the client has submitted, the
 * client interview is over: anything the readiness review surfaces is persisted
 * server-side by /api/pre-submit-review (review + request items) and becomes an
 * officer-side request round — never a further client message.
 *
 * This is the single decision the client effect consults with the LIVE submitted
 * state (a ref set synchronously at submission, plus the durable submittedAt),
 * not the stale closure value. Pure and harnessable.
 *
 * Returns:
 *   'ask_client'        — not submitted and the review raised points → ask in chat
 *   'defer_to_officer'  — the client has submitted → the points ride the officer
 *                         request round; post nothing to the client
 *   'noop'              — not submitted and the review raised nothing
 */
export function preSubmitReviewAction({ questionsCount = 0, submitted = false } = {}) {
  if (submitted) return 'defer_to_officer';
  return (Number(questionsCount) > 0) ? 'ask_client' : 'noop';
}

/*
 * THE SOFT CLOSE CARD READS THE STORED ITEMS (v1_759).
 *
 * /api/pre-submit-review writes the interview's request items server side
 * (state asked_in_interview, round 0) and returns only the review. The "Before
 * you submit" card is drawn from the client's LOCAL collected_data.requestItems,
 * which never received them (update-session rightly keeps the client from
 * writing them), so on a first visit the card never appeared and the client was
 * never offered add it now, send by a date, not available with a reason, or
 * someone else has it (end to end walk runs 2, 3, 4 and 6). The client now reads
 * the stored items back after the review (client-read, resource session).
 *
 * storedRequestItems: the items from a client-read session row, or null when the
 * read gave none (the caller keeps what it has and says so in the console).
 * softCloseItems: the one rule the card uses; null when the card is not shown.
 */
export function storedRequestItems(row) {
  const items = row && row.collected_data && row.collected_data.requestItems;
  return Array.isArray(items) ? items : null;
}

/**
 * v1_767: does this assistant message invite the client to type SUBMIT? The
 * pre-submit review, the soft close card and the Submit button all keyed on the
 * literal **SUBMIT**; when Alex bolded the whole sentence ("**If you are happy
 * with everything above, please type SUBMIT to send your file...**", walk run 11)
 * none of the three appeared, silently. SUBMIT in capitals inside a bold span is
 * the invitation; the lower case word "submit" is not.
 */
// 12/10/2026 (product decision): only the bold SUBMIT line counts. "type SUBMIT"
// in plain text no longer does: the Xapo replay's "It only goes forward once you
// type SUBMIT", answering "Has my file been sent?", started the one readiness
// review early, and its questions then landed after the closing summary.
const SUBMIT_INVITE = /\*\*[^*]*\bSUBMIT\b[^*]*\*\*/;
export function invitesSubmit(content) {
  return SUBMIT_INVITE.test(String(content || ''));
}

export function softCloseItems({ requestItems, lastMessage } = {}) {
  const list = Array.isArray(requestItems) ? requestItems : [];
  if (!list.some(i => i && i.state === 'asked_in_interview')) return null;
  if (!lastMessage || lastMessage.role !== 'assistant' || !invitesSubmit(lastMessage.content)) return null;
  return list.filter(i => i && ['asked_in_interview', 'promised', 'not_applicable', 'answered'].includes(i.state) && i.round === 0);
}

/*
 * ONE LIST FOR ALEX AND THE CARD (v1_872).
 *
 * Alex's closing summary lists the items still outstanding, and that summary is
 * what triggers the pre-submit review. The card reads the stored request items.
 * When the two differ, the chat tells the client something is outstanding that
 * the card does not offer (walks w1d006b8d and wc5a14861: five items in the chat,
 * no card). The review therefore stores what Alex listed as items too, and Alex's
 * later summaries are given the stored list to use.
 *
 * outstandingFromSummary: the bullet lines under an "outstanding" heading in
 * Alex's closing message (markdown stripped), in order, at most 8. A heading is a
 * line naming outstanding items, items still needed or to follow; the list ends at
 * the first non bullet line after a bullet, or at the next heading. Pure.
 */
const OUTSTANDING_HEAD = /(outstanding|still (need|needed|to come|required|to follow)|to follow|yet to (be )?(receive|provide)|items? (we )?(still )?need)/i;
const BULLET = /^\s*(?:[-*•]|\d{1,2}[.)])\s+(.*\S)\s*$/;
const HEADING = /^\s*(?:#{1,6}\s+|\*\*[^*]+\*\*\s*:?\s*$|[A-Z][A-Za-z /&]{2,60}:\s*$)/;
const clean = (s) => String(s || '').replace(/\*\*|__|`/g, '').replace(/\s+/g, ' ').trim();
export function outstandingFromSummary(text) {
  const lines = String(text || '').split(/\r?\n/);
  const out = [];
  let inList = false, sawBullet = false;
  for (const line of lines) {
    const b = line.match(BULLET);
    if (!inList) {
      if (!b && OUTSTANDING_HEAD.test(line) && line.trim().length <= 120) { inList = true; sawBullet = false; }
      continue;
    }
    if (b) { const t = clean(b[1]); if (t.length >= 8 && !/\bSUBMIT\b/.test(t)) out.push(t); sawBullet = true; continue; }
    if (!line.trim()) { if (sawBullet) inList = false; continue; }
    if (HEADING.test(line) || sawBullet) { inList = OUTSTANDING_HEAD.test(line) && !b; sawBullet = false; continue; }
  }
  return out.slice(0, 8);
}

/*
 * The instruction Alex's closing summary is given once items are stored: list
 * exactly these as outstanding (the soft close card offers the same ones).
 * Returns '' when nothing is stored. Pure.
 */
export function storedOutstandingClause(requestItems) {
  const list = (Array.isArray(requestItems) ? requestItems : []).filter(i => i && i.round === 0 && ['asked_in_interview', 'promised'].includes(i.state));
  if (!list.length) return '';
  return "\n\nOUTSTANDING ITEMS ON THE CLIENT'S CHECKLIST: when your summary lists what is still outstanding, list exactly these items and no others, in these words or close to them, because the checklist card the client sees offers exactly these:\n" + list.map((i, n) => `${n + 1}. ${i.label}`).join('\n');
}
